// Client Dashboard PAGE-CONTENT theme layout data — SINGLE layout, no
// picker/dropdown, same model as dashboardThemeLayouts.constants.js (see
// that file's header comment for the full rationale: this component is pure
// chrome/theme config, not browsable per-brand content, so each brand's
// admin must see only its own colors automatically, with no manual step).
//
// Per-section granularity — unchanged from the original single-layout
// version:
//   - overviewHeaderColor / overviewCardBgColor -> Overview's header banner + card body
//   - overviewOrdersTableBgColor/TextColor      -> Overview's orders-summary table
//   - activeOrdersBgColor/TextColor             -> My Orders page
//   - pastOrdersBgColor/TextColor                -> Past Orders page
//   - profileBgColor/TextColor                   -> Profile page (whole-page bg)
//   - devicesBgColor/TextColor                   -> Devices page (whole-page bg)
//
// Explicitly NOT included: header-color fields for My Orders/Past Orders/
// Profile/Devices, stat-tile accent colors, payment-status badge colors,
// per-device-row-card background, ExcludedIngredientsManager's own card.
//
// Text colors default to a neutral near-black (#1E1E1E) for every brand —
// none of the 3 brands' real sites use a colored body-text scheme for this
// kind of dense content (orders/forms/lists), so there's no real per-brand
// text color to translate here, unlike backgrounds. Background/header colors
// use each brand's own real palette:
//   - MEELZ: cream/brown (#F9F3E6 / #573121)
//   - E4F:   white/red (#f7f7f7 / #b11212)
//   - CK:    lavender/purple (#9D91B9 / #46298A)

const NEUTRAL_TEXT_COLOR = "#1E1E1E";

const BRAND_DEFAULT_PROPS_BY_CODE = {
    MEELZ: {
        overviewHeaderColor:          "#573121",
        overviewHeaderTextColor:      "#ffffff",
        overviewCardBgColor:          "#ffffff",
        overviewProfileTextColor:    NEUTRAL_TEXT_COLOR,
        overviewOrdersTableBgColor:   "#ffffff",
        overviewOrdersTableTextColor: NEUTRAL_TEXT_COLOR,
        activeOrdersBgColor:          "#ffffff",
        activeOrdersTextColor:        NEUTRAL_TEXT_COLOR,
        pastOrdersBgColor:            "#ffffff",
        pastOrdersTextColor:          NEUTRAL_TEXT_COLOR,
        profileBgColor:               "#F9F3E6",
        profileTextColor:             NEUTRAL_TEXT_COLOR,
        devicesBgColor:               "#F9F3E6",
        devicesTextColor:             NEUTRAL_TEXT_COLOR,
    },
    E4F: {
        overviewHeaderColor:          "#b11212",
        overviewHeaderTextColor:      "#ffffff",
        overviewCardBgColor:          "#ffffff",
        overviewProfileTextColor:    NEUTRAL_TEXT_COLOR,
        overviewOrdersTableBgColor:   "#ffffff",
        overviewOrdersTableTextColor: NEUTRAL_TEXT_COLOR,
        activeOrdersBgColor:          "#ffffff",
        activeOrdersTextColor:        NEUTRAL_TEXT_COLOR,
        pastOrdersBgColor:            "#ffffff",
        pastOrdersTextColor:          NEUTRAL_TEXT_COLOR,
        profileBgColor:               "#f7f7f7",
        profileTextColor:             NEUTRAL_TEXT_COLOR,
        devicesBgColor:               "#f7f7f7",
        devicesTextColor:             NEUTRAL_TEXT_COLOR,
    },
    CK: {
        overviewHeaderColor:          "#46298A",
        overviewHeaderTextColor:      "#ffffff",
        overviewCardBgColor:          "#ffffff",
        overviewProfileTextColor:    NEUTRAL_TEXT_COLOR,
        overviewOrdersTableBgColor:   "#ffffff",
        overviewOrdersTableTextColor: NEUTRAL_TEXT_COLOR,
        activeOrdersBgColor:          "#ffffff",
        activeOrdersTextColor:        NEUTRAL_TEXT_COLOR,
        pastOrdersBgColor:            "#ffffff",
        pastOrdersTextColor:          NEUTRAL_TEXT_COLOR,
        // profileBgColor/devicesBgColor were previously "#F9F3E6" — an exact
        // copy-paste of MEELZ's value above. The live client's own fallback
        // here is just bg-white (no real CK-branded color exists in the
        // client code to extract), so this seed default is set to CK's
        // lavender brand accent (#9D91B9, matching navbar/sidebar) for
        // consistency instead of carrying over MEELZ's color by mistake.
        profileBgColor:               "#9D91B9",
        profileTextColor:             NEUTRAL_TEXT_COLOR,
        devicesBgColor:               "#9D91B9",
        devicesTextColor:             NEUTRAL_TEXT_COLOR,
    },
};

const brandCode = process.env.REACT_APP_PROJECT_CODE || "CK";

// DEMO (and any future unmapped brand) falls back to MEELZ's colors — same
// fallback every other unmapped-brand component already has, not a new gap.
export const DASHBOARD_PAGES_THEME_DEFAULT_PROPS =
    BRAND_DEFAULT_PROPS_BY_CODE[brandCode] || BRAND_DEFAULT_PROPS_BY_CODE.MEELZ;

// See dashboardThemeLayouts.constants.js's identical export for why this is
// needed — the never-edited backfill check in componentRegistry.js must
// recognize ANY brand's untouched raw default, not just the active
// session's, under the current shared testing DB.
export const DASHBOARD_PAGES_THEME_ALL_BRAND_DEFAULTS = BRAND_DEFAULT_PROPS_BY_CODE;

export const DASHBOARD_PAGES_THEME_LAYOUTS = {
    default: { label: "Dashboard Pages", defaultProps: DASHBOARD_PAGES_THEME_DEFAULT_PROPS },
};

export const DASHBOARD_PAGES_THEME_SCHEMA = [
    { key: "overviewHeaderColor",          label: "Overview — header banner",       type: "color" },
    { key: "overviewHeaderTextColor",      label: "Overview — header text color",   type: "color" },
    { key: "overviewCardBgColor",          label: "Overview — profile card background", type: "color" },
    { key: "overviewProfileTextColor",     label: "Overview — profile card text", type: "color" },
    { key: "overviewOrdersTableBgColor",   label: "Overview — orders table background", type: "color" },
    { key: "overviewOrdersTableTextColor", label: "Overview — orders table text", type: "color" },
    { key: "activeOrdersBgColor",          label: "My Orders — background",   type: "color" },
    { key: "activeOrdersTextColor",        label: "My Orders — text",         type: "color" },
    { key: "pastOrdersBgColor",            label: "Past Orders — background", type: "color" },
    { key: "pastOrdersTextColor",          label: "Past Orders — text",       type: "color" },
    { key: "profileBgColor",               label: "Profile — background",     type: "color" },
    { key: "profileTextColor",             label: "Profile — text",           type: "color" },
    { key: "devicesBgColor",               label: "Devices — background",     type: "color" },
    { key: "devicesTextColor",             label: "Devices — text",           type: "color" },
];
